/**
 * Error-code registry for @norkron/web-offline-store.
 *
 * Side-effect import: registers the lib identity token (preventing
 * `LIB_NAME_UNREGISTERED` meta-warnings) and structured warning codes with
 * `@norkron/web-errors`. Importing once at module load is sufficient.
 */

import { createScopedErrors, registerCodes } from '@norkron/web-errors'

export const WEB_OFFLINE_STORE_LIB = '@norkron/web-offline-store'

export const WEB_OFFLINE_STORE_TOKEN = Symbol.for('norkron.lib.web-offline-store')

export const { warn, panic, reportInvalidInput } = createScopedErrors(WEB_OFFLINE_STORE_LIB, WEB_OFFLINE_STORE_TOKEN)

try {
	registerCodes(WEB_OFFLINE_STORE_LIB, {
		WEB_OFFLINE_STORE_INVALID_INPUT: {
			description:
				'Runtime input validation rejected the value passed to a public web-offline-store export (Wave 10 zod mandate). Payload follows WEB_INVALID_INPUT_SHAPE: { fieldPath, expected, received }.',
			severity: 'warning',
			docsAnchor: 'web-offline-store/invalid-input',
			exampleFix:
				'Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.',
		},
		WEB_OFFLINE_STORE_IDB_UNAVAILABLE: {
			description:
				'IndexedDB was requested but is unavailable (SSR, disabled storage, private-mode block, or no `indexedDB` global). Store operations reject with OfflineStoreError(code="idb-unavailable").',
			severity: 'warning',
			docsAnchor: 'web-offline-store/idb-unavailable',
			exampleFix:
				'Guard store creation behind a feature check, or run inside a browser context where `globalThis.indexedDB` exists.',
		},
		WEB_OFFLINE_STORE_MIGRATION_FAILED: {
			description:
				'A versioned migration step threw during the IndexedDB `upgradeneeded` transaction. The whole upgrade is aborted by the browser (atomic) so the DB stays at its prior version.',
			severity: 'error',
			docsAnchor: 'web-offline-store/migration-failed',
			exampleFix:
				'Fix the throwing migration; migrations must be idempotent and tolerate partial prior application. Bump schema.version only after the migration is correct.',
		},
		WEB_OFFLINE_STORE_VERSION_CONFLICT: {
			description:
				'Optimistic-concurrency check failed: the row was mutated by another writer between read and write (stored __version != expected). The update was NOT applied.',
			severity: 'warning',
			docsAnchor: 'web-offline-store/version-conflict',
			exampleFix:
				'Re-read the row, re-apply the mutation against the fresh value, and retry update(id, freshVersion, mutator).',
		},
		WEB_OFFLINE_STORE_QUOTA_LOW: {
			description:
				'navigator.storage.estimate() reports usage at or above the configured low-water mark (default 80%). A `storage-low` event was dispatched on the store.',
			severity: 'warning',
			docsAnchor: 'web-offline-store/quota-low',
			exampleFix:
				'Listen for the `storage-low` event and evict stale rows, or request additional quota via persist().',
		},
		WEB_OFFLINE_STORE_TX_ABORTED: {
			description:
				'A multi-store transaction was rolled back because its callback threw or the browser aborted it (quota exceeded, constraint error). No partial writes are visible.',
			severity: 'warning',
			docsAnchor: 'web-offline-store/tx-aborted',
			exampleFix:
				'Inspect the rejected error.cause; if quota-related, free space and retry the whole tx (transactions are all-or-nothing).',
		},
		WEB_OFFLINE_STORE_DECRYPT_FAILED: {
			description:
				'AES-GCM decryption of a stored payload failed (wrong passphrase, tampered ciphertext, or unencrypted legacy row read with an encrypting store).',
			severity: 'error',
			docsAnchor: 'web-offline-store/decrypt-failed',
			exampleFix:
				'Verify the passphrase matches the one used at write time. Encrypted and plaintext rows must not be mixed in one table.',
		},
		OFFLINE_STORE_SELFCHECK_INTERNAL_ERROR: {
			description:
				'selfCheck threw internally while inspecting an @norkron/web-offline-store instance. Exception caught + reported as a finding.',
			severity: 'info',
			docsAnchor: 'web-offline-store/self-check#internal-error',
			exampleFix: 'Inspect finding.message + finding.extras for the underlying error.',
		},
	})
} catch {
	// Re-registration with a different shape would throw; benign on hot reload.
}
